import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Container,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Tooltip,
  Skeleton,
  Stack,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import {
  Search,
  Trash2,
  Eye,
  Smartphone,
  FileText,
  Globe,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Images,
  LayoutTemplate,
  Sparkles,
  X,
} from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import TemplateStatusChip from "../../widgets/TemplateStatusChip";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

// Helper for status styling
const getStatusProps = (status) => {
  const s = (status || "pending").toLowerCase();
  switch (s) {
    case "approved":
      return { color: "success", icon: <CheckCircle2 size={16} /> };
    case "rejected":
      return { color: "error", icon: <XCircle size={16} /> };
    case "pending":
      return { color: "warning", icon: <Clock size={16} /> };
    default:
      return { color: "default", icon: <Clock size={16} /> };
  }
};

const getTemplateName = (template) =>
  template?.templateName || template?.name || "Untitled template";

const getTemplateStatus = (template) =>
  template?.templateStatus || template?.status || "pending";

const getTemplateDate = (template) =>
  template?.createdAt ||
  template?.createdOn ||
  template?.updatedAt ||
  template?.updatedOn;

const formatTemplateDate = (dateValue, includeTime = false) => {
  if (!dateValue) return "Not available";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Not available";

  return includeTime
    ? date.toLocaleString("en-IN")
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const getTemplateTypeLabel = (template) => {
  const type = template?.templateType || "standard";
  if (type === "carouselCard") return "Carousel";
  if (type === "richCard") return "Rich card";
  return type.replace(/_/g, " ");
};

const isCarouselTemplate = (template) =>
  template?.templateType === "carouselCard" ||
  Array.isArray(template?.content?.cardContents) ||
  Boolean(template?.content?.richCardDetails?.carousel);

const RcsTemplates = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const { data, isLoading, isError, refetch } = useApiEp(
    `/rcs/get-templates?sq=${encodeURIComponent(searchQuery)}`
  );
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  const templates = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  useEffect(() => {
    setSelectedTemplate((currentTemplate) => {
      if (templates.length === 0) return null;
      const refreshedSelection = currentTemplate
        ? templates.find((template) => template._id === currentTemplate._id)
        : null;
      return refreshedSelection || templates[0];
    });
  }, [templates]);

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      await fetch(
        `${API_ENDPOINT}/rcs/delete-template/${templateToDelete._id}`,
        {
          method: "DELETE",
          ...getHeader(),
        }
      );
      refetch();
      if (selectedTemplate?._id === templateToDelete._id) {
        setSelectedTemplate(null);
      }
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error("Error deleting template:", err);
      alert("Failed to delete template. Please try again.");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f8fc", py: { xs: 2, md: 3 } }}>
      <Container maxWidth="xl">
        <BackBtn />

        <Paper
          elevation={0}
          sx={{
            mt: 2,
            mb: 2.5,
            p: { xs: 2.25, md: 3 },
            border: "1px solid #e0e5f0",
            borderRadius: 3.5,
            background:
              "linear-gradient(135deg, #fbfaff 0%, #f1f5ff 58%, #eefbf8 100%)",
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                color: "#fff",
                background: "linear-gradient(135deg, #6d4bd1, #4f46e5)",
                boxShadow: "0 10px 24px rgba(109, 75, 209, 0.22)",
                flexShrink: 0,
              }}
            >
              <Sparkles size={25} />
            </Box>
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 24, md: 29 },
                  lineHeight: 1.15,
                  fontWeight: 800,
                  color: "#172033",
                  letterSpacing: "-0.025em",
                }}
              >
                RCS Templates
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                Find, review, and preview every message experience before launch.
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search by template name"
            value={searchInput}
            onChange={handleSearchChange}
            inputProps={{ "aria-label": "Search RCS templates by name" }}
            sx={{
              maxWidth: { md: 390 },
              bgcolor: "white",
              borderRadius: 2.25,
              "& .MuiOutlinedInput-root": { borderRadius: 2.25 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={19} color="#64748b" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear template search"
                    onClick={() => {
                      setSearchInput("");
                      setSearchQuery("");
                    }}
                  >
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Paper>

        <TemplatesList
          isMobile={isMobile}
          templates={templates}
          selectedTemplate={selectedTemplate}
          isLoading={isLoading}
          isError={isError}
          searchQuery={searchQuery}
          setSelectedTemplate={setSelectedTemplate}
          setTemplateToDelete={setTemplateToDelete}
          setDeleteDialogOpen={setDeleteDialogOpen}
        />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AlertCircle size={24} color="#d32f2f" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template{" "}
            <strong>"{templateToDelete?.templateName}"</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

const TemplatesList = ({
  isMobile,
  templates,
  selectedTemplate,
  isLoading,
  isError,
  searchQuery,
  setSelectedTemplate,
  setTemplateToDelete,
  setDeleteDialogOpen,
}) => {
  if (isLoading) return <TemplateListSkeleton />;

  if (isError) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
        Your RCS templates could not be loaded right now. Please try again
        shortly.
      </Alert>
    );
  }

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={4}>
        <Paper
          elevation={0}
          sx={{
            height: isMobile ? "420px" : "76vh",
            maxHeight: isMobile ? "420px" : "760px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: "#fafaff",
              borderBottom: "1px solid #e7eaf1",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LayoutTemplate size={18} color="#6d4bd1" />
              <Typography
                variant="subtitle1"
                component="h2"
                fontWeight="750"
                color="#1e293b"
              >
                Templates
              </Typography>
              <Chip
                label={templates.length}
                size="small"
                sx={{
                  ml: "auto",
                  height: 23,
                  minWidth: 30,
                  bgcolor: "#ede9fe",
                  color: "#5b4bb7",
                  fontWeight: 800,
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Select a template to inspect its message preview
            </Typography>
            {searchQuery && (
              <Typography variant="caption" sx={{ color: "#6d4bd1", mt: 0.5 }}>
                Results for "{searchQuery}"
              </Typography>
            )}
          </Box>

          {templates.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileText
                size={48}
                color="#bdbdbd"
                style={{ marginBottom: 16 }}
              />
              <Typography variant="body1" color="text.secondary">
                {searchQuery
                  ? "No templates found matching your search."
                  : "No templates available."}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, flex: 1, overflow: "auto" }}>
              {templates.map((template, index) => {
                const status = getTemplateStatus(template);
                const statusProps = getStatusProps(status);
                const carousel = isCarouselTemplate(template);

                return (
                  <React.Fragment key={template._id}>
                    <ListItem
                      button
                      selected={selectedTemplate?._id === template._id}
                      onClick={() => setSelectedTemplate(template)}
                      sx={{
                        px: 1.75,
                        py: 1.5,
                        transition: "all 0.2s",
                        borderLeft: "4px solid transparent",
                        "&:hover": { bgcolor: "#f8f9fc" },
                        "&.Mui-selected": {
                          bgcolor: "#f1effb",
                          borderLeftColor: "#6d4bd1",
                          "&:hover": { bgcolor: "#eeeafa" },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48 }}>
                        <Avatar
                          sx={{
                            bgcolor: carousel ? "#eaf8f2" : "#ede9fe",
                            color: carousel ? "#087a55" : "#6d4bd1",
                            width: 40,
                            height: 40,
                          }}
                        >
                          {carousel ? <Images size={20} /> : <FileText size={20} />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            noWrap
                          >
                            {getTemplateName(template)}
                          </Typography>
                        }
                        secondary={
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={getTemplateTypeLabel(template).toUpperCase()}
                              size="small"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                            <Chip
                              icon={statusProps.icon}
                              label={status}
                              size="small"
                              color={statusProps.color}
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                textTransform: "capitalize",
                                pl: 0.5,
                              }}
                            />
                          </Box>
                        }
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Tooltip title="Preview">
                          <IconButton
                            edge="end"
                            size="small"
                            color="primary"
                            aria-label={`Preview ${getTemplateName(template)}`}
                          >
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTemplateToDelete(template);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    {index < templates.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        {selectedTemplate ? (
          <RcsTemplatePreview template={selectedTemplate} />
        ) : (
          <Paper
            elevation={0}
            sx={{
              height: isMobile ? 400 : "76vh",
              maxHeight: isMobile ? 400 : 760,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <Smartphone
              size={64}
              color="#e0e0e0"
              style={{ marginBottom: 16 }}
            />
            <Typography variant="h6" color="text.secondary">
              {templates.length === 0
                ? "No templates to preview"
                : "Select a template to view details"}
            </Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

const getSuggestionLabel = (suggestion) =>
  suggestion?.action?.text ||
  suggestion?.action?.plainText ||
  suggestion?.reply?.plainText ||
  suggestion?.text ||
  "Action";

const CardMediaPreview = ({ card }) => {
  const media = card?.media || card?.cardMedia || {};
  const contentInfo = media.contentInfo || {};
  const fileUrl = contentInfo.fileUrl || card?.imageUrl;
  const thumbnailUrl = contentInfo.thumbnailUrl;
  const fileType = String(media.fileType || card?.fileType || "").toUpperCase();

  if (fileType === "VIDEO" && fileUrl) {
    return (
      <Box
        component="video"
        src={fileUrl}
        poster={thumbnailUrl}
        controls
        preload="metadata"
        aria-label={card?.title || card?.cardTitle || "RCS card video"}
        sx={{
          width: "100%",
          height: 150,
          display: "block",
          objectFit: "cover",
          bgcolor: "#111827",
        }}
      />
    );
  }

  const imageUrl = thumbnailUrl || fileUrl;
  if (!imageUrl) return null;

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={card?.title || card?.cardTitle || "RCS card media"}
      sx={{
        width: "100%",
        height: 150,
        display: "block",
        objectFit: "cover",
        bgcolor: "#eef2f7",
      }}
    />
  );
};

const SuggestionButtons = ({ suggestions = [] }) => {
  if (!suggestions.length) return null;

  return (
    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
      {suggestions.map((suggestion, index) => (
        <Button
          key={`${getSuggestionLabel(suggestion)}-${index}`}
          variant="outlined"
          size="small"
          tabIndex={-1}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: 11.5,
            fontWeight: 700,
            pointerEvents: "none",
          }}
        >
          {getSuggestionLabel(suggestion)}
        </Button>
      ))}
    </Stack>
  );
};

const MessageCardPreview = ({ card, cardNumber }) => {
  const title = card?.title || card?.cardTitle;
  const description = card?.description || card?.cardDescription;

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 248,
        width: 248,
        overflow: "hidden",
        border: "1px solid #e1e5ed",
        borderRadius: 2.5,
        scrollSnapAlign: "start",
        bgcolor: "#fff",
      }}
    >
      <CardMediaPreview card={card} />
      <Box sx={{ p: 1.5 }}>
        {cardNumber && (
          <Typography
            sx={{
              mb: 0.65,
              fontSize: 10,
              fontWeight: 800,
              color: "#6d4bd1",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Card {cardNumber}
          </Typography>
        )}
        {title && (
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.35, fontWeight: 750 }}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            sx={{
              mt: 0.75,
              fontSize: 11.5,
              lineHeight: 1.5,
              color: "text.secondary",
              whiteSpace: "pre-line",
            }}
          >
            {description}
          </Typography>
        )}
        <SuggestionButtons suggestions={card?.suggestions || []} />
      </Box>
    </Paper>
  );
};

export const RcsTemplatePreview = ({ template }) => {
  const theme = useTheme();

  const getTemplateContent = () => {
    if (!template?.content) return null;

    if (
      template.templateType === "carouselCard" ||
      Array.isArray(template.content.cardContents)
    ) {
      return {
        type: "carousel",
        cards: template.content.cardContents || [],
        suggestions: template.content.suggestions || [],
      };
    }

    if (template?.templateType === "standard") {
      const c = template.content;
      if (
        c.documentUrl ||
        template.subtemplateType === "text_message_with_media"
      ) {
        const fileName = c.documentFileName || "attachment";
        const isPdf = fileName.toLowerCase().endsWith(".pdf");
        return {
          type: "textWithMedia",
          documentUrl: c.documentUrl,
          documentFileName: fileName,
          messageText: c.messageText || "",
          messageOrder: c.messageOrder || "text_message_at_top",
          suggestions: c.suggestions || [],
          isPdf,
        };
      }
      if (c.messageText) {
        return {
          type: "plain",
          text: c.messageText,
          suggestions: c.suggestions || [],
        };
      }
    }

    if (template.templateType === "richCard") {
      const card = template.content.cardContent || template.content;
      return {
        type: "standalone",
        card: {
          ...card,
          title: card.title || card.messageText,
          description: card.description || card.messageDescription,
          media:
            card.media ||
            (card.imageUrl
              ? {
                  contentInfo: { fileUrl: card.imageUrl },
                  fileType: card.fileType || "IMAGE",
                }
              : undefined),
        },
        suggestions: card.suggestions || [],
      };
    }

    if (template.content.plainText) {
      return {
        type: "plain",
        text: template.content.plainText,
        suggestions: template.content.suggestions || [],
      };
    }

    if (template.content.richCardDetails) {
      const rc = template.content.richCardDetails;
      if (rc.standalone) {
        return {
          type: "standalone",
          card: rc.standalone.content,
          orientation: rc.standalone.cardOrientation,
          suggestions: rc.standalone.content?.suggestions || [],
        };
      }
      if (rc.carousel) {
        return {
          type: "carousel",
          cards: rc.carousel.contents || [],
          suggestions: rc.carousel.suggestions || [],
        };
      }
    }
    return null;
  };

  const content = getTemplateContent();
  const templateName = getTemplateName(template);
  const templateStatus = getTemplateStatus(template);
  const isRejected = templateStatus.toLowerCase() === "rejected";

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.75, sm: 3 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: "#fff",
      }}
    >
      {/* Header Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" fontWeight="700" gutterBottom>
          {templateName}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
          <Chip
            icon={<Globe size={16} />}
            label={getTemplateTypeLabel(template)}
            variant="outlined"
            size="small"
            sx={{ pl: 0.5 }}
          />
          <Chip
            icon={<Calendar size={16} />}
            label={formatTemplateDate(getTemplateDate(template))}
            variant="outlined"
            size="small"
            sx={{ pl: 0.5 }}
          />
          <TemplateStatusChip status={templateStatus} />
        </Box>
      </Box>

      {/* Rejection Reason Alert */}
      {isRejected && (
        <Alert
          severity="error"
          icon={<AlertCircle size={24} />}
          sx={{ mb: 4, borderRadius: 2, alignItems: "center" }}
        >
          <Typography variant="subtitle2" fontWeight="600">
            Template Rejected
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {template.comment ||
              template.rejectionReason ||
              "No specific reason provided by the reviewer."}
          </Typography>
        </Alert>
      )}

      {/* Simulated Phone UI Preview */}
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: 340,
          border: "12px solid #2c2c2c",
          borderRadius: "36px",
          bgcolor: "#f5f6f8",
          position: "relative",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Fake Phone Notch */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 120,
            height: 24,
            bgcolor: "#2c2c2c",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            zIndex: 10,
          }}
        />

        <Box
          sx={{
            p: 2,
            pt: 4,
            bgcolor: "white",
            color: "text.primary",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" textAlign="center">
            {templateName}
          </Typography>
        </Box>

        {/* --- SCROLLABLE PHONE CONTENT AREA --- */}
        <Box
          sx={{
            p: 2,
            height: 480,
            overflowY: "auto",
            // Custom thin scrollbar so it looks native to mobile
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {content ? (
            <Card
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 3,
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {/* Text + Media */}
              {content.type === "textWithMedia" && (
                <Box>
                  {content.messageText &&
                    content.messageOrder === "text_message_at_top" && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-line", mb: 2, lineHeight: 1.6 }}
                      >
                        {content.messageText}
                      </Typography>
                    )}

                  {content.documentUrl && (
                    <Box sx={{ mb: 2 }}>
                      {content.isPdf ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            borderRadius: 2,
                            textAlign: "center",
                            bgcolor: "grey.50",
                          }}
                        >
                          <FileText
                            size={48}
                            color="#f44336"
                            style={{ marginBottom: 12 }}
                          />
                          <Typography variant="subtitle2" fontWeight="bold">
                            {content.documentFileName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            PDF Document
                          </Typography>
                        </Paper>
                      ) : (
                        <Box
                          component="img"
                          src={content.documentUrl}
                          alt={content.documentFileName}
                          sx={{
                            width: "100%",
                            maxHeight: 200,
                            objectFit: "cover", // better than contain for mobile cards if we want a full bleed, or keep contain based on preference
                            borderRadius: 2,
                            bgcolor: "#000",
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {content.messageText &&
                    content.messageOrder !== "text_message_at_top" && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-line", mt: 2, lineHeight: 1.6 }}
                      >
                        {content.messageText}
                      </Typography>
                    )}
                </Box>
              )}

              {/* Plain Text */}
              {content.type === "plain" && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.5 }}
                >
                  {content.text}
                </Typography>
              )}

              {content.type === "standalone" && content.card && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <MessageCardPreview card={content.card} />
                </Box>
              )}

              {content.type === "carousel" && (
                <Box>
                  <Box
                    sx={{
                      mb: 1.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Images size={16} color="#6d4bd1" />
                      <Typography sx={{ fontSize: 12, fontWeight: 750 }}>
                        Carousel preview
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={`${content.cards.length} cards`}
                      sx={{
                        height: 22,
                        bgcolor: "#ede9fe",
                        color: "#5b4bb7",
                        fontSize: 10.5,
                        fontWeight: 750,
                      }}
                    />
                  </Box>

                  {content.cards.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        mx: -1,
                        px: 1,
                        pb: 1,
                        overflowX: "auto",
                        scrollSnapType: "x mandatory",
                        "&::-webkit-scrollbar": { height: 5 },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: "#cbd2df",
                          borderRadius: 4,
                        },
                      }}
                    >
                      {content.cards.map((card, index) => (
                        <MessageCardPreview
                          key={`${card.title || card.cardTitle || "card"}-${index}`}
                          card={card}
                          cardNumber={index + 1}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      sx={{ py: 3, textAlign: "center", fontSize: 12, color: "text.secondary" }}
                    >
                      This carousel does not contain any cards.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Suggestions Chips */}
              {content.suggestions?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {content.suggestions.map((suggestion, idx) => (
                      <Chip
                        key={idx}
                        label={
                          getSuggestionLabel(suggestion)
                        }
                        variant="outlined"
                        size="medium"
                        color="primary"
                        sx={{ borderRadius: 4, fontWeight: "500" }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Card>
          ) : (
            <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
              <Typography variant="body2">
                No preview content available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Tech Details Section */}
      <Box
        sx={{
          mt: 5,
          p: 3,
          bgcolor: "grey.50",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight="600"
          gutterBottom
          sx={{ mb: 2 }}
        >
          Metadata
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Template ID
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {template.templateId || template.id || "Not available"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Customer ID
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {template.customerId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              User ID
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {template.userId || template.email || "Not available"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Updated On
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {formatTemplateDate(
                template.updatedAt || template.updatedOn || getTemplateDate(template),
                true
              )}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const TemplateListSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton
        variant="rectangular"
        width={250}
        height={32}
        sx={{ mb: 2, borderRadius: 1 }}
      />
      <Skeleton
        variant="rectangular"
        width={400}
        height={40}
        sx={{ mb: 4, borderRadius: 2 }}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #eee",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Skeleton variant="rectangular" height={64} />
            {[...Array(4)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #eee",
                }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ mr: 2 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Skeleton
            variant="rectangular"
            height={500}
            sx={{ borderRadius: 3 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RcsTemplates;
